// PWA install plumbing. The public MainLayout checks `canShowInstallPrompt()`
// on first access and renders <InstallPrompt> if true; the mobile field flow
// also surfaces it contextually.

const DISMISS_KEY = 'wesafe.installDismissedAt';
const DISMISS_WINDOW_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let captureRegistered = false;

// Call once on app boot. Idempotent.
export function setupPwaInstallCapture(): void {
  if (captureRegistered || typeof window === 'undefined') return;
  captureRegistered = true;
  window.addEventListener('beforeinstallprompt', (e) => {
    // Stash for later — the browser only fires this once per page load,
    // and only before the user dismisses it via the native banner.
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    // Clear any prior dismissal so a reinstall reopens the prompt path.
    try {
      localStorage.removeItem(DISMISS_KEY);
    } catch {
      // ignore quota / private-mode errors
    }
  });
}

export function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  // iPadOS 13+ reports as Mac; check maxTouchPoints to disambiguate.
  const isIosLike = /iPhone|iPad|iPod/.test(ua) || (ua.includes('Mac') && navigator.maxTouchPoints > 1);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isIosLike && isSafari;
}

export function isInStandaloneMode(): boolean {
  if (typeof window === 'undefined') return false;
  // iOS exposes navigator.standalone; everyone else uses the display-mode MQ.
  // Cast — navigator.standalone is non-standard so TS doesn't type it.
  const iosStandalone = (window.navigator as { standalone?: boolean }).standalone === true;
  const mqStandalone = window.matchMedia?.('(display: standalone)').matches === true;
  return iosStandalone || mqStandalone;
}

export function canShowInstallPrompt(): boolean {
  if (typeof window === 'undefined') return false;
  if (isInStandaloneMode()) return false;
  // Suppressed within the dismissal window.
  try {
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) ?? '0');
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_WINDOW_MS) return false;
  } catch {
    // ignore
  }
  // Either we have a captured Android prompt event, or we're on iOS Safari
  // (which never fires the event but can install via Add-to-Home-Screen).
  return deferredPrompt !== null || isIosSafari();
}

export async function triggerNativeInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferredPrompt) return 'unavailable';
  await deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  deferredPrompt = null;
  return outcome;
}

export function markInstallPromptDismissed(): void {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

export function hasDeferredPrompt(): boolean {
  return deferredPrompt !== null;
}
